import React, { useState, useMemo } from 'react';
import { TouchableWithoutFeedback, View } from 'react-native';

import { isEmpty as _isEmpty, isFunction as _isFunction, isNil as _isNil } from 'lodash';
import PropTypes from 'prop-types';

import { VFText, VFButton, ErrorScreen, Icon } from '@vfgroup-oneplatform/foundation/Components';

import { Images } from '../../Themes';
import SohoFrameworkTestKeys from '../../Utils/SohoFrameworkTestKeys';
import { ADDONS_MY_PROFILE_DISPLAY_TYPES } from '../Utils/Enums';

import styles from './AddOns.Styles';
import { AddOnsList, AddOnsCard } from './Components';
import { addonPropType } from './Components/AddOnsList/AddOnsList';
import AddOnsLoading from './Components/AddOnsLoading/AddOnsLoading';

function getInfoBasedOnDisplayType(displayType, title) {
  switch (displayType) {
    case ADDONS_MY_PROFILE_DISPLAY_TYPES.MY_ADDONS:
      return {
        title: title ?? 'soho_addons_table_title',
        subTitle: 'soho_my_profile_addons_view_requests_label',
        icon: Images.ic_folder_red,
      };

    case ADDONS_MY_PROFILE_DISPLAY_TYPES.REQUESTS:
    default:
      return {
        title: title ?? 'soho_my_profile_addons_requested_addons_title',
        subTitle: 'soho_my_profile_addons_my_addons_label',
        icon: Images.ic_category,
      };
  }
}

function getLoadingAndErrorBasedOnDisplayType(
  displayType,
  isMyAddOnsLoading,
  myAddOnsError,
  isRequestedAddOnsLoading,
  requestedAddOnsError,
) {
  switch (displayType) {
    case ADDONS_MY_PROFILE_DISPLAY_TYPES.MY_ADDONS:
      return {
        isLoading: isMyAddOnsLoading,
        error: myAddOnsError,
      };

    case ADDONS_MY_PROFILE_DISPLAY_TYPES.REQUESTS:
    default:
      return {
        isLoading: isRequestedAddOnsLoading,
        error: requestedAddOnsError,
      };
  }
}

function renderRecommendation({
  recommendation,
  onRecommendedAddOnsPress,
  withUsers,
  images,
  isCurrencyFirst,
}) {
  if (_isEmpty(recommendation)) {
    return null;
  }

  return (
    <View style={styles.recommendedStyle}>
      <AddOnsCard
        item={recommendation}
        isRecommended
        leftIconSize={42}
        onPress={() => {
          if (_isFunction(onRecommendedAddOnsPress)) {
            onRecommendedAddOnsPress(recommendation);
          }
        }}
        images={images}
        withUsers={withUsers}
        isCurrencyFirst={isCurrencyFirst}
      />
    </View>
  );
}

function renderActiveAddOns(
  activeAddOns,
  onActiveAddOnsPress,
  withUsers,
  images,
  emptyActiveAddOnsTitle,
  emptyActiveAddOnsTitlePlaceHolders,
  isCurrencyFirst,
) {
  if (_isEmpty(activeAddOns)) {
    return (
      <VFText
        testKey={SohoFrameworkTestKeys.AddOnsActiveAddonsEmptyText}
        i18nKey={emptyActiveAddOnsTitle}
        placeHolders={emptyActiveAddOnsTitlePlaceHolders}
        style={styles.emptyCaseText}
      />
    );
  }
  return (
    <AddOnsList
      title="soho_addons_table_active_addons"
      data={activeAddOns}
      onAddOnPress={onActiveAddOnsPress}
      images={images}
      withUsers={withUsers}
      isCurrencyFirst={isCurrencyFirst}
    />
  );
}

function renderExpiredAddOns(
  expiredAddOns,
  onExpiredAddOnsPress,
  withUsers,
  images,
  isCurrencyFirst,
) {
  if (_isEmpty(expiredAddOns)) {
    return null;
  }
  return (
    <AddOnsList
      title="soho_addons_table_expired_addons"
      data={expiredAddOns}
      isExpired={true}
      containerStyle={styles.expiredAddOnsContainerStyle}
      onAddOnPress={onExpiredAddOnsPress}
      images={images}
      withUsers={withUsers}
      isCurrencyFirst={isCurrencyFirst}
    />
  );
}

function switchDisplayType(activeDisplayType, setDisplayType, onDisplayTypeChange) {
  const newDisplayTypes =
    activeDisplayType === ADDONS_MY_PROFILE_DISPLAY_TYPES.MY_ADDONS
      ? ADDONS_MY_PROFILE_DISPLAY_TYPES.REQUESTS
      : ADDONS_MY_PROFILE_DISPLAY_TYPES.MY_ADDONS;

  setDisplayType(newDisplayTypes);
  if (_isFunction(onDisplayTypeChange)) {
    onDisplayTypeChange(newDisplayTypes);
  }
}

function renderDisplayChangeButton(
  withDisplayChange,
  subTitle,
  icon,
  onDisplayTypeChange,
  displayType,
  setDisplayType,
) {
  if (withDisplayChange) {
    return (
      <View style={styles.displayChangeButtonContainer}>
        <TouchableWithoutFeedback
          onPress={() => switchDisplayType(displayType, setDisplayType, onDisplayTypeChange)}
          testID={SohoFrameworkTestKeys.AddOnsDisplayChangeBtn}
        >
          <View style={styles.subTitleContainerStyle}>
            <VFText i18nKey={subTitle} style={styles.subTitleTextStyle} />
            <Icon name={icon} size={25} />
          </View>
        </TouchableWithoutFeedback>
      </View>
    );
  }
}

function renderLoading(withTray, withUsers, withDisplayChange, displayType) {
  return (
    <AddOnsLoading
      withTray={withTray}
      withUsers={withUsers}
      withDisplayChange={withDisplayChange}
      displayType={displayType}
      testID={SohoFrameworkTestKeys.AddOnsLoading}
    />
  );
}

function renderError(error, onTryAgainPress, displayType, errorIconStyle) {
  return (
    <ErrorScreen
      {...error}
      iconStyle={errorIconStyle}
      text={error?.message}
      onPress={() => onTryAgainPress(displayType)}
      testID={SohoFrameworkTestKeys.AddOnsError}
    />
  );
}

function renderMyAddOns(
  displayType,
  activeAddOns,
  onActiveAddOnsPress,
  expiredAddOns,
  onExpiredAddOnsPress,
  emptyAddOnsTitle,
  emptyAddOnsTitlePlaceHolders,
  emptyActiveAddOnsTitle,
  emptyActiveAddOnsTitlePlaceHolders,
  withUsers,
  images,
  isCurrencyFirst,
) {
  if (displayType === ADDONS_MY_PROFILE_DISPLAY_TYPES.MY_ADDONS) {
    if (_isEmpty(activeAddOns) && _isEmpty(expiredAddOns)) {
      return (
        <VFText
          i18nKey={emptyAddOnsTitle}
          placeHolders={emptyAddOnsTitlePlaceHolders}
          style={styles.emptyCaseText}
        />
      );
    }
    return (
      <>
        {renderActiveAddOns(
          activeAddOns,
          onActiveAddOnsPress,
          withUsers,
          images,
          emptyActiveAddOnsTitle,
          emptyActiveAddOnsTitlePlaceHolders,
          isCurrencyFirst,
        )}
        {renderExpiredAddOns(
          expiredAddOns,
          onExpiredAddOnsPress,
          withUsers,
          images,
          isCurrencyFirst,
        )}
      </>
    );
  }
}

function renderRequestedAddOns(
  displayType,
  requestedAddOns,
  onRequestedAddOnsPress,
  withUsers,
  images,
  emptyRequestedAddOnsPlaceHolders,
  isCurrencyFirst,
) {
  if (displayType === ADDONS_MY_PROFILE_DISPLAY_TYPES.REQUESTS) {
    if (_isEmpty(requestedAddOns)) {
      return (
        <VFText
          testKey={SohoFrameworkTestKeys.MyProfileRequestedAddOnsEmptyText}
          i18nKey={'soho_my_profile_requested_addon_empty_text'}
          placeHolders={emptyRequestedAddOnsPlaceHolders}
          style={styles.emptyCaseText}
        />
      );
    }
    return (
      <AddOnsList
        data={requestedAddOns}
        onAddOnPress={onRequestedAddOnsPress}
        withUsers={withUsers}
        images={images}
        containerStyle={styles.requestedAddOnsContainerStyle}
        isCurrencyFirst={isCurrencyFirst}
      />
    );
  }
}

function renderAddOns(
  displayType,
  activeAddOns,
  onActiveAddOnsPress,
  expiredAddOns,
  onExpiredAddOnsPress,
  requestedAddOns,
  onRequestedAddOnsPress,
  emptyAddOnsTitle,
  emptyAddOnsTitlePlaceHolders,
  emptyActiveAddOnsTitle,
  emptyActiveAddOnsTitlePlaceHolders,
  emptyRequestedAddOnsPlaceHolders,
  withUsers,
  images,
  isCurrencyFirst,
) {
  return (
    <>
      {renderMyAddOns(
        displayType,
        activeAddOns,
        onActiveAddOnsPress,
        expiredAddOns,
        onExpiredAddOnsPress,
        emptyAddOnsTitle,
        emptyAddOnsTitlePlaceHolders,
        emptyActiveAddOnsTitle,
        emptyActiveAddOnsTitlePlaceHolders,
        withUsers,
        images,
        isCurrencyFirst,
      )}

      {renderRequestedAddOns(
        displayType,
        requestedAddOns,
        onRequestedAddOnsPress,
        withUsers,
        images,
        emptyRequestedAddOnsPlaceHolders,
        isCurrencyFirst,
      )}
    </>
  );
}

function renderContent({
  withTray,
  recommendation,
  onRecommendedAddOnsPress,
  primaryButtonTitle,
  onBuyAddOnsPress,
  title,
  titlePlaceHolders,
  withDisplayChange,
  subTitle,
  icon,
  onDisplayTypeChange,
  displayType,
  setDisplayType,
  activeAddOns,
  onActiveAddOnsPress,
  expiredAddOns,
  onExpiredAddOnsPress,
  requestedAddOns,
  onRequestedAddOnsPress,
  emptyAddOnsTitle,
  emptyAddOnsTitlePlaceHolders,
  emptyActiveAddOnsTitle,
  emptyActiveAddOnsTitlePlaceHolders,
  emptyRequestedAddOnsPlaceHolders,
  error,
  onTryAgainPress,
  withUsers,
  images,
  isCurrencyFirst,
}) {
  return (
    <View style={styles.containerStyle(withTray)} testID={SohoFrameworkTestKeys.AddOnsDataView}>
      {renderRecommendation({
        recommendation,
        onRecommendedAddOnsPress,
        withUsers,
        images,
        isCurrencyFirst,
      })}

      <VFButton
        title={primaryButtonTitle}
        style={styles.buttonStyle}
        type="primary"
        onPress={onBuyAddOnsPress}
      />

      <View style={styles.titleContainerStyle}>
        <VFText
          i18nKey={title}
          placeHolders={titlePlaceHolders}
          type="primary"
          style={styles.titleTextStyle}
        />

        {renderDisplayChangeButton(
          withDisplayChange,
          subTitle,
          icon,
          onDisplayTypeChange,
          displayType,
          setDisplayType,
        )}
      </View>

      {!_isNil(error)
        ? renderError(error, onTryAgainPress, displayType, styles.errorIconStyle)
        : renderAddOns(
            displayType,
            activeAddOns,
            onActiveAddOnsPress,
            expiredAddOns,
            onExpiredAddOnsPress,
            requestedAddOns,
            onRequestedAddOnsPress,
            emptyAddOnsTitle,
            emptyAddOnsTitlePlaceHolders,
            emptyActiveAddOnsTitle,
            emptyActiveAddOnsTitlePlaceHolders,
            emptyRequestedAddOnsPlaceHolders,
            withUsers,
            images,
            isCurrencyFirst,
          )}
    </View>
  );
}
function AddOns({
  titlePlaceHolders,
  recommendation,
  activeAddOns,
  expiredAddOns,
  onBuyAddOnsPress,
  onRecommendedAddOnsPress,
  onActiveAddOnsPress,
  onExpiredAddOnsPress,
  withTray,
  isLoading: isMyAddOnsLoading,
  error: myAddOnsError,
  onTryAgainPress,
  images,
  withUsers,
  primaryButtonTitle,
  withDisplayChange,
  onDisplayTypeChange,
  requestedAddOns,
  isRequestedAddOnsLoading,
  requestedAddOnsError,
  onRequestedAddOnsPress,
  emptyAddOnsTitle,
  emptyAddOnsTitlePlaceHolders,
  emptyActiveAddOnsTitle,
  emptyActiveAddOnsTitlePlaceHolders,
  emptyRequestedAddOnsPlaceHolders,
  isCurrencyFirst,
  ...props
}) {
  const [displayType, setDisplayType] = useState(ADDONS_MY_PROFILE_DISPLAY_TYPES.MY_ADDONS);

  const { icon, subTitle, title } = useMemo(
    () => getInfoBasedOnDisplayType(displayType, props.title),
    [displayType],
  );

  const { isLoading, error } = getLoadingAndErrorBasedOnDisplayType(
    displayType,
    isMyAddOnsLoading,
    myAddOnsError,
    isRequestedAddOnsLoading,
    requestedAddOnsError,
  );

  return (
    <>
      {isLoading
        ? renderLoading(withTray, withUsers, withDisplayChange, displayType)
        : renderContent({
            withTray,
            recommendation,
            onRecommendedAddOnsPress,
            primaryButtonTitle,
            onBuyAddOnsPress,
            title,
            titlePlaceHolders,
            withDisplayChange,
            subTitle,
            icon,
            onDisplayTypeChange,
            displayType,
            setDisplayType,
            activeAddOns,
            onActiveAddOnsPress,
            expiredAddOns,
            onExpiredAddOnsPress,
            requestedAddOns,
            onRequestedAddOnsPress,
            emptyAddOnsTitle,
            emptyAddOnsTitlePlaceHolders,
            emptyActiveAddOnsTitle,
            emptyActiveAddOnsTitlePlaceHolders,
            emptyRequestedAddOnsPlaceHolders,
            error,
            onTryAgainPress,
            withUsers,
            images,
            isCurrencyFirst,
          })}
    </>
  );
}

const recommendationPropType = PropTypes.shape({
  users: PropTypes.array,
  id: PropTypes.string,
  name: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  validFor: PropTypes.shape({
    endDate: PropTypes.any,
    startDate: PropTypes.any,
  }),
  price: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currency: PropTypes.string,
  }),
});

AddOns.defaultProps = {
  title: 'soho_addons_table_title',
  emptyAddOnsTitlePlaceHolders: [],
  withUsers: true,
  withDisplayChange: false,
  primaryButtonTitle: 'soho_addons_buy_addons_button_title',
  isCurrencyFirst: false,
};

AddOns.propTypes = {
  title: PropTypes.string,
  titlePlaceHolders: PropTypes.arrayOf(PropTypes.string),
  recommendation: recommendationPropType,
  activeAddOns: PropTypes.arrayOf(addonPropType),
  expiredAddOns: PropTypes.arrayOf(addonPropType),
  withTray: PropTypes.bool,
  onBuyAddOnsPress: PropTypes.func,
  onRecommendedAddOnsPress: PropTypes.func,
  onActiveAddOnsPress: PropTypes.func,
  onExpiredAddOnsPress: PropTypes.func,
  images: PropTypes.object,
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
  isLoading: PropTypes.bool,
  onTryAgainPress: PropTypes.func,
  withUsers: PropTypes.bool,
  primaryButtonTitle: PropTypes.string,
  withDisplayChange: PropTypes.bool,
  onDisplayTypeChange: PropTypes.func,
  requestedAddOns: PropTypes.array,
  onRequestedAddOnsPress: PropTypes.func,
  emptyAddOnsTitle: PropTypes.string,
  emptyAddOnsTitlePlaceHolders: PropTypes.arrayOf(PropTypes.string),
  emptyActiveAddOnsTitle: PropTypes.string,
  emptyActiveAddOnsTitlePlaceHolders: PropTypes.arrayOf(PropTypes.string),
  emptyRequestedAddOnsPlaceHolders: PropTypes.arrayOf(PropTypes.string),
  isRequestedAddOnsLoading: PropTypes.bool,
  requestedAddOnsError: PropTypes.object,
  isCurrencyFirst: PropTypes.bool,
};

export {
  renderExpiredAddOns,
  renderContent,
  switchDisplayType,
  renderRecommendation,
  getInfoBasedOnDisplayType,
};
export default AddOns;
